import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Multi-image uploader.
 * Props:
 *   value: string[]              — current image URLs
 *   onChange: (urls) => void     — called whenever the list changes
 *   max?: number                 — max images (default 6)
 */
export default function ImageUploader({ value = [], onChange, max = 6 }) {
  const { API, authHeader } = useAuth();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  // Convert backend-relative URL to absolute for <img src>
  const toSrc = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

  const uploadFiles = async (files) => {
    const list = Array.from(files).slice(0, Math.max(0, max - value.length));
    if (list.length === 0) {
      toast.error(`Max ${max} images`);
      return;
    }
    setBusy(true);
    const urls = [];
    try {
      for (const f of list) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 5 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads/image`, fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      if (urls.length) {
        onChange([...value, ...urls]);
        toast.success(`Uploaded ${urls.length} image${urls.length > 1 ? "s" : ""}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="image-uploader" className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {value.map((u, i) => (
          <div key={i} className="relative aspect-[3/4] bg-[#F3EFEA] group" data-testid={`uploader-img-${i}`}>
            <img src={toSrc(u)} alt={`Product ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`uploader-remove-${i}`}
              className="absolute top-1 right-1 bg-[#FAFAF7]/90 backdrop-blur p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
            {i === 0 && (
              <span className="absolute bottom-1 left-1 bg-[#6B1A24] text-[#FAFAF7] text-[9px] uppercase tracking-[0.2em] px-2 py-0.5">Main</span>
            )}
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            disabled={busy}
            data-testid="uploader-add"
            className="aspect-[3/4] border-2 border-dashed border-[#D1C7BD] flex flex-col items-center justify-center gap-2 text-[#5C5454] hover:border-[#6B1A24] hover:text-[#6B1A24] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={20} className="animate-spin" /> : <UploadCloud size={20} strokeWidth={1.5} />}
            <span className="text-[10px] uppercase tracking-[0.2em]">{busy ? "Uploading" : "Add image"}</span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        data-testid="uploader-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={(e) => e.target.files && uploadFiles(e.target.files)}
        className="hidden"
      />
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C5454]">
        JPG · PNG · WEBP · up to 5 MB each · max {max} images · drag & drop supported
      </p>
    </div>
  );
}
